/**
 * ModuleEnquiryModal — a logged-in client enquires about a module they don't
 * have. Posts to /api/account/module-enquiry which lands in the operator work
 * queue AND emails the operator so they can set it up.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Lock, Send, Sparkles } from 'lucide-react';
import { toast } from '@/utils/safeToast';
import { authFetch } from '@/utils/authFetch';
import { API_URL } from '@/config/apiConfig';

export const ModuleEnquiryModal = ({ open, onOpenChange, moduleName, moduleId }) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [alreadySent, setAlreadySent] = useState(false);

  const submit = async () => {
    setSending(true);
    try {
      const res = await authFetch(`${API_URL}/api/account/module-enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ module_id: moduleId || null, module_name: moduleName, message: message.trim() || null }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(body.detail || 'Could not send your enquiry.'); return; }
      setAlreadySent(body.status === 'existing');
      setSent(true);
      toast.success(body.message || `We've logged your interest in ${moduleName}.`);
    } catch {
      toast.error('Something went wrong sending your enquiry.');
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    // reset after the close animation
    setTimeout(() => { setMessage(''); setSent(false); setAlreadySent(false); }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md" data-testid="module-enquiry-modal">
        {!sent ? (
          <>
            <DialogHeader>
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center mb-2">
                <Lock className="w-5 h-5 text-white" />
              </div>
              <DialogTitle data-testid="module-enquiry-title">Enquire about {moduleName}</DialogTitle>
              <DialogDescription>
                This module isn't on your plan yet. Send us a quick note and our team will set it up and get in touch.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={`Tell us how you'd like to use ${moduleName}… (optional)`}
              rows={4}
              data-testid="module-enquiry-message"
              className="resize-none"
            />
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="ghost" onClick={close} data-testid="module-enquiry-cancel">Cancel</Button>
              <Button onClick={submit} disabled={sending} data-testid="module-enquiry-submit" className="bg-gradient-to-r from-teal-500 to-blue-600 text-white hover:opacity-90">
                {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send enquiry</>}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="text-center py-4" data-testid="module-enquiry-success">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <Sparkles className="w-6 h-6 text-emerald-600" />
            </div>
            <DialogTitle className="text-center">{alreadySent ? 'Already enquired' : 'Enquiry sent'}</DialogTitle>
            <DialogDescription className="text-center mt-1">
              {alreadySent ? (
                <>You've already enquired about <strong>{moduleName}</strong> — our team will be in touch shortly.</>
              ) : (
                <>Thanks! We've logged your interest in <strong>{moduleName}</strong> and our team will reach out shortly.</>
              )}
            </DialogDescription>
            <Button onClick={close} className="mt-5 w-full" data-testid="module-enquiry-done">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ModuleEnquiryModal;
